/**
 * Hook to select and transform chat messages from Redux to UI-ready ChatItem format.
 */

import type { ChatMessageEntity } from '@saaga/shared/store/slices/chat-message-entity.interface'
import {
  selectHasMoreMessages,
  selectIsOrchestrationActive,
  selectMessagesForConversation,
} from '@saaga/shared/store/slices/chat-selectors.store'
import { useAppSelector } from '@saaga/shared/store/store'
import { useMemo } from 'react'
import type {
  ChatItem,
  ChatThinking,
  ChatUserInteraction,
} from '@/components/chat/chat-message.interface'
import { transformEntityToChatItem } from './chat-message-entity-transformers.helper'

interface UseChatMessageSelectorsParams {
  conversationId: string | null
  userInteractionCardList: ChatUserInteraction[]
}

interface UseChatMessageSelectorsReturn {
  itemList: ChatItem[]
  hasMoreMessages: boolean
}

const getStepCount = (item: ChatItem): number | undefined =>
  'stepCount' in item ? (item.stepCount as number | undefined) : undefined

const sortByTimestamp = (itemList: ChatItem[]): ChatItem[] =>
  [...itemList].sort((a, b) => {
    const stepA = getStepCount(a)
    const stepB = getStepCount(b)
    if (stepA != null && stepB != null && stepA !== stepB) return stepA - stepB

    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  })

const isEmptyLoadingReport = (item: ChatItem): boolean =>
  item.type === 'agent-report' && !item.reportContent

const isVisibleItem = (item: ChatItem): boolean => {
  if (isEmptyLoadingReport(item)) return false
  return true
}

interface AssembleItemsParams {
  messageList: ChatMessageEntity[]
  isOrchestrationActive: boolean
  userInteractionCardList: ChatUserInteraction[]
}

const assembleItemList = ({
  messageList,
  isOrchestrationActive,
  userInteractionCardList,
}: AssembleItemsParams): ChatItem[] => {
  const sortedMessageList = sortByTimestamp(messageList.map(transformEntityToChatItem)).filter(
    isVisibleItem
  )

  sortedMessageList.push(...userInteractionCardList)

  if (isOrchestrationActive) {
    const thinkingIndicator: ChatThinking = {
      id: 'thinking-indicator',
      type: 'thinking',
      timestamp: new Date().toISOString(),
    }
    sortedMessageList.push(thinkingIndicator)
  }

  return sortedMessageList
}

export const useChatMessageSelectors = ({
  conversationId,
  userInteractionCardList,
}: UseChatMessageSelectorsParams): UseChatMessageSelectorsReturn => {
  const messageList = useAppSelector((state) =>
    selectMessagesForConversation(state, conversationId ?? 'pending')
  )
  const isOrchestrationActive = useAppSelector((state) =>
    selectIsOrchestrationActive(state, conversationId ?? 'pending')
  )
  const itemList = useMemo(
    () =>
      assembleItemList({
        messageList,
        isOrchestrationActive,
        userInteractionCardList,
      }),
    [messageList, isOrchestrationActive, userInteractionCardList]
  )
  const hasMoreMessages = useAppSelector((state) =>
    selectHasMoreMessages(state, conversationId ?? 'pending')
  )
  return { itemList, hasMoreMessages }
}
